import { Link } from 'react-router-dom';
import { getPublished } from '@/data/loader';
import { useI18n } from '@/hooks/useI18n';
import { useTranslation } from '@/hooks/useTranslation';
import { EntityCard } from '@/components/EntityCard';
import { RestaurantesSEO } from '@/components/SEO';
import { Container } from '@/components/Container';
import { OptimizedImage } from '@/components/OptimizedImage';
import { RichText } from '@/components/RichText';
import { RestauranteQuiz } from '@/components/RestauranteQuiz';
import { AuthorBox } from '@/components/AuthorBox';
import { defaultAuthor } from '@/data/author';

const VILAS = [
  { slug: 'encantadas', name: 'Encantadas' },
  { slug: 'nova-brasilia', name: 'Nova Brasília' },
  { slug: 'farol', name: 'Praia do Farol' },
  { slug: 'praia-grande', name: 'Praia Grande' },
] as const;

const PROFILES = ['frutos-do-mar', 'japones', 'cafe', 'pe-na-areia', 'familia', 'romantico'] as const;
const PROFILE_ICONS: Record<string, string> = {
  'frutos-do-mar': '🦐',
  japones: '🍣',
  cafe: '☕',
  'pe-na-areia': '🏖️',
  familia: '👨‍👩‍👧',
  romantico: '💑',
};
const FAQ_COUNT = 8;
const TIPS_COUNT = 4;

/** Nota a exibir: prioriza TripAdvisor (/5); fallback Google (/5) */
function nota(p: any): { val: string; max: string; src: string } | null {
  if (p.rating?.tripAdvisor) return { val: Number(p.rating.tripAdvisor).toFixed(1), max: '5', src: 'TripAdvisor' };
  if (p.rating?.google) return { val: Number(p.rating.google).toFixed(1), max: '5', src: 'Google' };
  return null;
}

export function RestaurantesPage() {
  const { lang } = useI18n();
  const { t } = useTranslation();
  const restaurantes = getPublished('restaurante', lang);
  const top15 = restaurantes.filter((p: any) => p.featured).sort((a: any, b: any) => a.rank - b.rank);

  return (
    <>
      <RestaurantesSEO restaurantes={restaurantes} top={top15} />
      <div>
        {/* Hero */}
        <div className="relative mb-10">
          <div className="relative h-48 sm:h-56">
            <OptimizedImage
              src="/images/hero-v2.jpg"
              alt="Restaurantes na Ilha do Mel"
              className="w-full h-full object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-sand-100/95 via-sand-100/40 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 pb-6">
            <Container>
              <h1 className="font-display text-4xl sm:text-5xl text-warm-dark">{t('page.restaurantes.h1')}</h1>
            </Container>
          </div>
        </div>

        <Container className="pb-20">
          {/* Intro (estilo Jorsiley, com link para a home) */}
          <div className="max-w-3xl mb-14 font-body text-warm-gray leading-relaxed [&_p]:mb-4 [&_a]:text-teal-deep [&_a]:underline">
            <RichText text={t('page.restaurantes.intro')} />
          </div>

          {/* As 15 melhores — ranking */}
          <section className="mb-16" id="melhores">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">{t('page.restaurantes.top15.title')}</h2>
            <p className="font-body text-warm-gray mb-8 max-w-3xl">{t('page.restaurantes.top15.subtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {top15.map((p: any) => {
                const n = nota(p);
                const isPartner = p.type === 'restaurante-parceiro';
                return (
                  <article key={p.handle} className="relative bg-white border border-sand-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 flex flex-col">
                    <span className="absolute top-3 left-3 z-10 w-9 h-9 rounded-full bg-teal-deep text-white font-display text-lg flex items-center justify-center shadow">
                      {p.rank}
                    </span>
                    {isPartner && (
                      <span className="absolute top-3 right-3 z-10 bg-coral text-white text-[11px] font-body font-semibold px-2.5 py-1 rounded-full shadow">
                        ★ Parceiro
                      </span>
                    )}
                    <Link to={`/restaurante/${p.handle}`} className="block h-44 overflow-hidden">
                      <picture className="block w-full h-full">
                        <source srcSet={p.image.replace(/\.(jpe?g|png)$/i, '.webp')} type="image/webp" />
                        <img src={p.image} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" width={400} height={225} />
                      </picture>
                    </Link>
                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-1 text-xs font-body text-warm-gray">
                        <span>📍 {p.vilarejoName}</span>
                        {p.cuisine?.[0] && (
                          <span className="bg-sand-100 px-2 py-0.5 rounded-full">{p.cuisine[0]}</span>
                        )}
                      </div>
                      <h3 className="font-display text-lg text-warm-dark leading-tight mb-2">
                        <Link to={`/restaurante/${p.handle}`} className="hover:text-teal-deep transition-colors">{p.title}</Link>
                      </h3>
                      {n && (
                        <div className="flex items-center gap-1.5 mb-3 text-sm">
                          <span className="font-display text-teal-deep font-bold">{n.val}</span>
                          <span className="text-warm-gray text-xs">/{n.max} · {n.src}</span>
                          {p.faixaPreco && <span className="text-warm-gray text-xs ml-1">· {p.faixaPreco}</span>}
                        </div>
                      )}
                      <div className="mt-auto pt-2">
                        <Link to={`/restaurante/${p.handle}`} className="block w-full text-center font-body text-sm font-semibold bg-teal-deep hover:bg-teal-deep/90 text-white rounded-lg px-3 py-2.5 transition-colors">
                          Ver detalhes completos do restaurante
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          {/* Tabela comparativa do top 15 */}
          <section className="mb-16" id="comparativo">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">{t('page.restaurantes.compare.title')}</h2>
            <p className="font-body text-warm-gray mb-6">{t('page.restaurantes.compare.subtitle')}</p>
            <div className="overflow-x-auto rounded-xl border border-sand-200 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-teal-deep text-white">
                    <th className="text-left px-4 py-3 font-body font-semibold">#</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">{t('page.restaurantes.compare.colName')}</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">{t('page.restaurantes.compare.colVila')}</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">{t('page.restaurantes.compare.colCozinha')}</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">{t('page.restaurantes.compare.colDestaque')}</th>
                    <th className="text-left px-4 py-3 font-body font-semibold whitespace-nowrap">{t('page.restaurantes.compare.colNota')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-sand-100">
                  {top15.map((p: any, i: number) => {
                    const n = nota(p);
                    return (
                      <tr key={p.handle} className={i % 2 === 0 ? 'bg-white' : 'bg-sand-50'}>
                        <td className="px-4 py-3 font-display text-teal-deep font-bold">{p.rank}</td>
                        <td className="px-4 py-3 font-body font-medium text-teal-deep whitespace-nowrap">
                          <Link to={`/restaurante/${p.handle}`} className="hover:underline">{p.title}</Link>
                        </td>
                        <td className="px-4 py-3 font-body text-warm-gray">{p.vilarejoName}</td>
                        <td className="px-4 py-3 font-body text-warm-gray whitespace-nowrap">{(p.cuisine || []).slice(0, 2).join(', ') || '—'}</td>
                        <td className="px-4 py-3 font-body text-warm-gray">{p.pratosAssinatura?.[0] || '—'}</td>
                        <td className="px-4 py-3 font-body text-warm-gray whitespace-nowrap">{n ? `${n.val}/${n.max} ${n.src}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {/* Quiz interativo — cruza desejo do visitante × entrega do restaurante */}
          <RestauranteQuiz restaurantes={restaurantes} />

          {/* Todos os restaurantes por vila */}
          <section className="mb-16" id="todas">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2">{t('page.restaurantes.all.title')}</h2>
            <p className="font-body text-warm-gray mb-8 max-w-3xl">{t('page.restaurantes.all.subtitle')}</p>
            {VILAS.map(vila => {
              const list = restaurantes.filter((p: any) => p.vilarejo === vila.slug);
              if (!list.length) return null;
              return (
                <div key={vila.slug} className="mb-10">
                  <h3 className="font-display text-2xl text-warm-dark mb-1">{vila.name}</h3>
                  <p className="font-body text-sm text-warm-gray mb-5">
                    {list.length} {list.length === 1 ? 'restaurante' : 'restaurantes'} · {t(`page.restaurantes.vilaDesc.${vila.slug}`)}
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map((p: any) => (
                      <EntityCard key={p.handle} entity={p} category="restaurante" />
                    ))}
                  </div>
                </div>
              );
            })}
          </section>

          {/* O que comer na ilha (pratos típicos) */}
          <section className="mb-16 bg-sand-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="o-que-comer">
            <div className="max-w-3xl">
              <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-4">{t('page.restaurantes.whatToEat.title')}</h2>
              <div className="font-body text-warm-gray leading-relaxed [&_p]:mb-4">
                <RichText text={t('page.restaurantes.whatToEat.intro')} />
              </div>
            </div>
          </section>

          {/* Por tipo de cozinha / ocasião */}
          <section className="mb-16" id="perfis">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-2 text-center">{t('page.restaurantes.profiles.title')}</h2>
            <p className="font-body text-warm-gray mb-10 text-center">{t('page.restaurantes.profiles.subtitle')}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {PROFILES.map(key => (
                <div key={key} className="bg-white rounded-xl p-6 shadow-sm border border-sand-200">
                  <div className="text-3xl mb-3">{PROFILE_ICONS[key]}</div>
                  <h3 className="font-display text-xl text-warm-dark mb-2">{t(`page.restaurantes.profiles.${key}.title`)}</h3>
                  <p className="font-body text-warm-gray text-sm leading-relaxed">{t(`page.restaurantes.profiles.${key}.desc`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Dicas práticas */}
          <section className="mb-16" id="dicas">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8">{t('page.restaurantes.tips.title')}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {Array.from({ length: TIPS_COUNT }, (_, i) => (
                <div key={i} className="flex gap-4 p-5 bg-teal-light/5 rounded-xl border border-teal-light/20">
                  <span className="text-teal-deep font-bold flex-shrink-0 mt-0.5">✓</span>
                  <p className="font-body text-warm-gray leading-relaxed">{t(`page.restaurantes.tips.${i}`)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="max-w-3xl" id="faq">
            <h2 className="font-display text-3xl sm:text-4xl text-warm-dark mb-8">{t('page.restaurantes.faq.title')}</h2>
            <div className="space-y-3">
              {Array.from({ length: FAQ_COUNT }, (_, i) => (
                <details key={i} className="group bg-white border border-sand-200 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between p-4 cursor-pointer hover:bg-sand-50 list-none">
                    <span className="font-display text-base text-warm-dark pr-4">{t(`page.restaurantes.faq.items.${i}.q`)}</span>
                    <span className="text-teal-deep flex-shrink-0 transition-transform duration-200 group-open:rotate-180">▼</span>
                  </summary>
                  <div className="px-4 pb-4 font-body text-warm-gray text-sm leading-relaxed">{t(`page.restaurantes.faq.items.${i}.a`)}</div>
                </details>
              ))}
            </div>
          </section>

          <AuthorBox author={defaultAuthor} />
        </Container>
      </div>
    </>
  );
}
